/**
 * The app's idle-session policy: log a session out once nobody has used it for
 * `sessionTimeoutMins`.
 *
 * `app/Router.tsx` installs it and owns what expiry means (logout + redirect to
 * /login?expired=1). Everything that counts as use calls `reset()`: an axios
 * response the user's own action caused (`services/api/http.ts`), and local
 * pointer/keyboard interaction (`hooks/useSessionActivity.ts`). Work that keeps
 * the session alive without producing either takes a `hold()` instead.
 */
interface SessionManager {
  init(
    sessionExpiredCallback: () => void,
    intervalTimeSeconds: number,
    sessionTimeoutMins: number,
  ): void
  reset(): void
  /**
   * Holds the session open until the returned release function is called, and
   * gives it a full idle window from that moment.
   *
   * For work that outlives the idle window while producing no activity of its
   * own — an album upload streaming browser → Filestack → S3. A hold is checked
   * before the clock is read, so it does not depend on any timer of its own
   * firing on schedule. That matters: a backgrounded tab is exactly where the
   * long uploads happen and exactly where timers are throttled hardest.
   *
   * Release is idempotent, so a double-release cannot drop somebody else's hold.
   */
  hold(): () => void
  immediateExpire(): void
  cleanUp(): void
}

let interval: NodeJS.Timeout
let sessionExpiredCallback: () => void

/**
 * When the session was last used, as a wall-clock timestamp.
 *
 * This used to be a count of elapsed ticks (`minutes += 1` per poll), which
 * measured how many times the browser let the timer run rather than how long
 * the user had been gone. The two diverge whenever the tab is throttled, and
 * they diverge completely when the machine sleeps: close a laptop lid with an
 * admin tab open and the count froze mid-window instead of expiring, so the tab
 * came back still logged in hours later. Reading the clock counts the time that
 * actually passed (AB#24880).
 */
let lastActivityAt = 0

// Outstanding hold() calls. Deliberately NOT cleared by init() or cleanUp():
// effects run child-first, so a re-init of the Router-level timer can land after
// a live uploader has already taken its hold, and clearing here would silently
// drop the protection the upload is relying on. A leaked hold keeps a session
// alive longer than it should; a dropped hold logs an admin out mid-upload. The
// first is the better failure, and React runs the effect cleanup that releases.
let holdCount = 0

const expireSession = (): void => {
  clearInterval(interval)
  sessionExpiredCallback()
}

export const sessionManager: SessionManager = {
  init: (
    cb: () => void,
    intervalTimeSeconds: number,
    sessionTimeoutMins,
  ): void => {
    sessionExpiredCallback = cb
    lastActivityAt = Date.now()

    const idleWindowMs = sessionTimeoutMins * 60 * 1000

    if (interval) {
      clearInterval(interval)
    }
    interval = setInterval(() => {
      if (holdCount > 0) {
        lastActivityAt = Date.now()
        return
      }

      // Strictly greater, so expiry lands on the first poll AFTER the window
      // closes rather than on the poll that reaches it — the same boundary the
      // tick-counting version had.
      if (Date.now() - lastActivityAt > idleWindowMs) {
        expireSession()
      }
    }, 1000 * intervalTimeSeconds)
  },
  reset: () => {
    lastActivityAt = Date.now()
  },
  hold: () => {
    holdCount += 1
    lastActivityAt = Date.now()

    let released = false

    return (): void => {
      if (released) {
        return
      }
      released = true
      holdCount -= 1
      // Finishing the work is itself use of the session, so the window starts
      // over rather than resuming wherever it stood when the hold was taken.
      lastActivityAt = Date.now()
    }
  },
  immediateExpire: () => {
    expireSession()
  },
  cleanUp: () => {
    clearInterval(interval)
  },
}
